import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell, Heart, Sparkles, MessageCircle, Check, UserPlus, UserCheck } from 'lucide-react';
import { Loading, EmptyState, ErrorState } from '../components/ui';
import { timeAgo } from '../utils';
import notificationService from '../services/notificationService';

export default function NotificationsPage({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!user?.uid) return;
    const unsub = notificationService.subscribe(
      user.uid,
      ({ notifications: notifs }) => {
        setNotifications(notifs);
        setError(false);
        setLoading(false);
      },
      () => {
        setError(true);
        setLoading(false);
      }
    );
    return unsub;
  }, [user?.uid]);

  const unread = notifications.filter((n) => !n.read);

  const handleMarkRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
    } catch (err) {
      console.error('Erreur markAsRead:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead(notifications);
    } catch (err) {
      console.error('Erreur markAllAsRead:', err);
    }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) handleMarkRead(notif.id);
    if (notif.type === 'match' && notif.conversationId) {
      navigate(`/messages/${notif.conversationId}`);
    } else if (notif.type === 'match') {
      navigate('/matches');
    } else if (notif.type === 'message') {
      navigate(`/messages/${notif.conversationId}`);
    } else if (notif.type === 'like') {
      navigate('/matches?tab=received');
    } else if (notif.type === 'friend_request') {
      navigate('/');
    } else if (notif.type === 'friend_accepted' && notif.conversationId) {
      navigate(`/messages/${notif.conversationId}`);
    } else if (notif.type === 'friend_accepted') {
      navigate('/members');
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return <Loading msg={t('common.loading')} />;
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-32 lg:pb-8">
        <ErrorState onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pt-24 pb-32 lg:pb-8">
      <Helmet>
        <title>{t('notifications.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/notifications" />
      </Helmet>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {t('notifications.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {unread.length > 0
              ? unread.length > 1
                ? t('notifications.unread_plural', { count: unread.length })
                : t('notifications.unread', { count: unread.length })
              : t('notifications.allRead')}
          </p>
        </div>
        {unread.length > 0 && (
          <button
            onClick={handleMarkAllRead}
            data-testid="notifs-btn-mark-all"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-rose-500 hover:text-rose-600 bg-rose-50 dark:bg-rose-950/30 hover:bg-rose-100 dark:hover:bg-rose-900/30 rounded-full transition-colors"
          >
            <Check className="w-4 h-4" />
            {t('notifications.markAllRead')}
          </button>
        )}
      </div>

      {/* Liste des notifications */}
      {notifications.length === 0 ? (
        <EmptyState
          icon={Bell}
          title={t('notifications.empty.title')}
          desc={t('notifications.empty.desc')}
          ctaLabel={t('notifications.empty.cta')}
          onCtaClick={() => navigate('/discover')}
        />
      ) : (
        <div className="space-y-2">
          {notifications.map((notif) => (
            <button
              key={notif.id}
              data-testid="notifs-item"
              onClick={() => handleNotifClick(notif)}
              className={`w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl text-left transition-all hover:shadow-md ${
                !notif.read
                  ? 'bg-white dark:bg-gray-900 border-2 border-rose-200 dark:border-rose-800 shadow-sm'
                  : 'bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 opacity-75 hover:opacity-100'
              }`}
            >
              {/* Icône */}
              <div
                className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                  notif.type === 'match'
                    ? 'bg-gradient-to-br from-purple-400 to-rose-400 text-white'
                    : notif.type === 'message'
                      ? 'bg-gradient-to-br from-blue-400 to-indigo-400 text-white'
                      : notif.type === 'friend_request'
                        ? 'bg-gradient-to-br from-violet-400 to-purple-500 text-white'
                        : notif.type === 'friend_accepted'
                          ? 'bg-gradient-to-br from-emerald-400 to-green-500 text-white'
                          : 'bg-gradient-to-br from-rose-400 to-rose-500 text-white'
                }`}
              >
                {notif.type === 'match' ? (
                  <Sparkles className="w-5 h-5" />
                ) : notif.type === 'message' ? (
                  <MessageCircle className="w-5 h-5" />
                ) : notif.type === 'friend_request' ? (
                  <UserPlus className="w-5 h-5" />
                ) : notif.type === 'friend_accepted' ? (
                  <UserCheck className="w-5 h-5" />
                ) : (
                  <Heart className="w-5 h-5 fill-current" />
                )}
              </div>

              {/* Contenu */}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm ${!notif.read ? 'text-gray-900 dark:text-gray-100 font-semibold' : 'text-gray-600 dark:text-gray-300'}`}
                >
                  {notif.type === 'match' ? (
                    notif.fromName ? (
                      t('notifications.types.match', { name: notif.fromName })
                    ) : (
                      t('notifications.types.matchDefault')
                    )
                  ) : notif.type === 'message' ? (
                    <>
                      <span className="font-bold">
                        {notif.fromName || t('notifDropdown.someone')}
                      </span>
                      {' : '}
                      {notif.messagePreview || t('notifications.types.message')}
                    </>
                  ) : notif.type === 'friend_request' ? (
                    t('notifications.types.friendRequest', {
                      name: notif.fromName || t('notifDropdown.someone'),
                    })
                  ) : notif.type === 'friend_accepted' ? (
                    t('notifications.types.friendAccepted', {
                      name: notif.fromName || t('notifDropdown.someone'),
                    })
                  ) : (
                    t('notifications.types.like')
                  )}
                </p>
                <p className="text-xs text-gray-400 mt-1">{timeAgo(notif.createdAt)}</p>
              </div>

              {/* Badge non lu */}
              {!notif.read && (
                <div className="w-3 h-3 bg-rose-500 rounded-full flex-shrink-0 animate-pulse" />
              )}

              {/* CTA */}
              <span
                className={`text-xs font-medium flex-shrink-0 ${
                  notif.type === 'match'
                    ? 'text-purple-500'
                    : notif.type === 'message'
                      ? 'text-blue-500'
                      : notif.type === 'friend_request'
                        ? 'text-violet-500'
                        : notif.type === 'friend_accepted'
                          ? 'text-emerald-500'
                          : 'text-rose-400'
                }`}
              >
                {t(
                  `notifications.cta.${notif.type === 'friend_request' ? 'friendRequest' : notif.type === 'friend_accepted' ? 'friendAccepted' : notif.type}`
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
